// Studio preview: the widget inside the Aevo widget studio's iframe. Takes
// config and state from the parent window (`preview-protocol.ts`), reports
// clicks on widget parts back, and never opens a socket (the panel checks
// `host.preview` before creating its chat client).

import {
  isAllowedPreviewOrigin,
  isPreviewPart,
  parsePreviewMessage,
  type PreviewInboundMessage,
  type PreviewOutboundMessage,
} from './preview-protocol'

export interface PreviewController {
  /** Post `preview-ready` (once the panel has rendered). */
  ready(version: string): void
  dispose(): void
}

export interface PreviewOptions {
  origins: readonly string[]
  shadowRoot: ShadowRoot
  onMessage(message: PreviewInboundMessage): void
}

/** Post to the parent window, once per allowed origin (only a match delivers). */
export function postToStudio(origins: readonly string[], message: PreviewOutboundMessage): void {
  if (typeof window === 'undefined' || window.parent === window) return
  for (const origin of origins) {
    try {
      window.parent.postMessage(message, origin)
    } catch {
      /* origin string rejected; skip it */
    }
  }
}

export function attachPreview(opts: PreviewOptions): PreviewController {
  const onWindowMessage = (event: MessageEvent): void => {
    if (!isAllowedPreviewOrigin(event.origin, opts.origins)) return
    if (window.parent !== window && event.source !== window.parent) return
    const message = parsePreviewMessage(event.data)
    if (message) opts.onMessage(message)
  }
  window.addEventListener('message', onWindowMessage)

  // A click anywhere on a `data-se-part` element tells the studio which
  // setting to focus. Prompt chips don't send anything in preview.
  const onClick = (event: Event): void => {
    for (const node of event.composedPath()) {
      if (!(node instanceof HTMLElement)) continue
      const part = node.dataset.sePart
      if (!isPreviewPart(part)) continue
      const index = node.dataset.seIndex !== undefined ? Number(node.dataset.seIndex) : undefined
      postToStudio(opts.origins, {
        type: 'aevo-echo:preview-select',
        part,
        ...(Number.isFinite(index) ? { index } : {}),
      })
      if (part === 'prompt') event.preventDefault()
      return
    }
  }
  opts.shadowRoot.addEventListener('click', onClick, true)

  return {
    ready(version) {
      postToStudio(opts.origins, { type: 'aevo-echo:preview-ready', version })
    },
    dispose() {
      window.removeEventListener('message', onWindowMessage)
      opts.shadowRoot.removeEventListener('click', onClick, true)
    },
  }
}
